/**
 * @voltxampere/engine — MOC3041-style zero-crossing optocoupler
 * (Sprint 54.2b-2 ADR-0087)
 *
 * Galvanic-isolated TRIAC driver — LED side electrically isolated
 * from photo-TRIAC side, coupling via simulated infra-red light.
 * Standard datasheet behavior (Vishay MOC3041, Fairchild MOC3052):
 *
 *   1. LED forward bias (I_LED > I_F_TRIG, typ 5-15 mA) "arms" the
 *      photo-TRIAC.
 *   2. Photo-TRIAC fires ONLY when |V(MT1−MT2)| crosses below the
 *      zero-crossing window V_ZC (typ 20V at mains).
 *   3. Once fired, photo-TRIAC latches like a normal TRIAC (Sprint
 *      54.2b-1 pattern reuse — holding current drop-out at V_hold).
 *
 * The galvanic isolation means the LED-side network and the photo-
 * TRIAC-side network are TWO INDEPENDENT CONNECTED COMPONENTS in the
 * MNA topology. Sprint 8b two-tier reduced-KCL convergence handles
 * isolated subgraphs naturally — each side projects its own residual.
 *
 * State plumbing (TRIAC pattern reuse):
 *   - dynamicAugments() returns ["internalNode"] — 1 row per MOC
 *   - State variable: 0 = photo off, 1 = photo conducting
 *   - DC stamp pins state to 0 (off in DC OP)
 *   - Transient stamp derives newState from xStepPrev:
 *
 *       I_LED_prev = max(0, (V_anode_prev − V_cathode_prev − V_F) · G_LED)
 *       ledTrigger = I_LED_prev > iFTrig
 *       inZeroCrossWindow = |V_mt1_prev − V_mt2_prev| < vZc
 *       stillLatched = prevState === 1 && |V_mt_diff_prev| > vHoldProxy
 *       newState = (ledTrigger && inZeroCrossWindow) || stillLatched ? 1 : 0
 *
 * LED branch model: simple voltage-controlled conductance G_LED with
 * forward-knee V_F. Stamped as 2-terminal linear resistor between
 * anode + cathode. Real LED diode physics not modeled at MVP — user
 * places a series limiting resistor externally (per datasheet
 * recommendation; ngspice MOC3041 subcircuit also uses simplified
 * LED stamp).
 *
 * Photo-TRIAC branch: TRIAC-style off/on stamping. Reuses stampOff
 * and stampOn patterns (locally duplicated for module independence;
 * Sprint 55+ refactor consolidates into shared utility).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { defineStamp } from "../definition.js";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
/**
 * Default photo-TRIAC OFF-state conductance (S). 1e-9 ≈ 1 GΩ —
 * matches MOC3041 V_DRM blocking impedance.
 */
const G_OFF_DEFAULT = 1e-9;
/** Default photo-TRIAC ON-state resistance (Ω). Higher than discrete
 * TRIAC because MOC3041 carries low current (max 100 mA RMS). */
const R_ON_DEFAULT = 5.0;
/** Default photo-TRIAC ON-state voltage drop (V). MOC3041 V_TM ≈ 3 V
 * at 100 mA. */
const V_ON_DEFAULT = 3.0;
/**
 * Default LED forward-knee voltage (V). MOC3041 V_F ≈ 1.3 V at
 * I_F = 10 mA (datasheet typ).
 */
const V_F_LED_DEFAULT = 1.3;
/**
 * Default LED side conductance above the forward knee (S). 0.01 S =
 * 100 Ω effective slope resistance. With external 360 Ω series
 * resistor (datasheet recommendation), 5 V drive gives ~10 mA, which
 * crosses I_F_TRIG.
 */
const G_LED_DEFAULT = 0.01;
/**
 * Default LED forward current trigger threshold (A). MOC3041 I_FT
 * (max) = 5 mA. Conservative default 5 mA.
 */
const I_F_TRIG_DEFAULT = 0.005;
/**
 * Default zero-crossing window (V). MOC3041 V_iH (inhibit voltage)
 * typ 20 V — photo-TRIAC only fires when |V_main| < V_ZC.
 */
const V_ZC_DEFAULT = 20.0;
/**
 * Default holding-voltage drop-out proxy (V). Same role as TRIAC
 * — collapses latch when |V_diff| < vHoldProxy.
 */
const V_HOLD_VOLTAGE_PROXY_DEFAULT = 1.0;
function nodeVoltage(xPrev, idx) {
    if (idx < 0)
        return 0;
    return xPrev[idx] ?? 0;
}
/**
 * Sprint 54.2b-2 — every MOC instance allocates exactly 1 internal
 * node row for photo-TRIAC state storage.
 */
function mocDynamicAugments(_component) {
    return ["internalNode"];
}
/**
 * Stamp the LED-side branch: linear conductance with forward-knee
 * Norton offset.
 *
 *   I_LED = max(0, (V_anode − V_cathode − V_F) · G_LED)
 *
 * Linearised for Newton:
 *
 *   Y[anode][anode] += G_LED, Y[cathode][cathode] += G_LED
 *   Y[anode][cathode] -= G_LED, Y[cathode][anode] -= G_LED
 *   rhs[anode] += V_F · G_LED  (forward-knee offset, only when forward-biased)
 *   rhs[cathode] -= V_F · G_LED
 *
 * Reverse-biased (V_anode < V_cathode + V_F): no current contribution.
 * We accept the discontinuity — Sprint 8b two-tier convergence
 * smooths it for Newton.
 */
function stampLedBranch(ctx, anode, cathode, gLed, vFLed) {
    // KVL companion: V_anode − V_cathode = V_F + I_LED / G_LED
    // → I_LED = G_LED · (V_anode − V_cathode − V_F)
    //
    // Conductance + always-emitted Norton offset. Below the knee
    // the linear model continues as a small negative reverse
    // conduction — not physical for a real LED but acceptable for
    // MVP because user circuits include a series limiting resistor
    // that prevents reverse bias. Sprint 54.3 parity validation
    // tightens this with an exponential Shockley curve if needed.
    //
    // Sprint 54.2b-2 lesson: conditional offset (only when forward
    // biased) caused Newton stuck-below-knee — at cold-start vPrev=0
    // → no offset → stamp degenerates to pure conductance → V_a
    // converges below V_F → still no offset, etc. Always emit.
    ctx.addY(anode, anode, gLed);
    ctx.addY(cathode, cathode, gLed);
    ctx.addY(anode, cathode, -gLed);
    ctx.addY(cathode, anode, -gLed);
    const ieq = vFLed * gLed;
    if (anode >= 0)
        ctx.rhs[anode] = (ctx.rhs[anode] ?? 0) + ieq;
    if (cathode >= 0)
        ctx.rhs[cathode] = (ctx.rhs[cathode] ?? 0) - ieq;
}
/** Photo-TRIAC OFF branch — high impedance between MT1 and MT2. */
function stampPhotoOffBranch(ctx, mt1, mt2, gOff) {
    ctx.addY(mt1, mt1, gOff);
    ctx.addY(mt2, mt2, gOff);
    ctx.addY(mt1, mt2, -gOff);
    ctx.addY(mt2, mt1, -gOff);
}
/** Photo-TRIAC ON branch — TRIAC-style Norton with bidirectional V_on. */
function stampPhotoOnBranch(ctx, mt1, mt2, rOn, vOn, vDiffPrev) {
    const gOn = 1 / rOn;
    ctx.addY(mt1, mt1, gOn);
    ctx.addY(mt2, mt2, gOn);
    ctx.addY(mt1, mt2, -gOn);
    ctx.addY(mt2, mt1, -gOn);
    const sign = vDiffPrev >= 0 ? 1 : -1;
    const ieq = (sign * vOn) / rOn;
    if (mt1 >= 0)
        ctx.rhs[mt1] = (ctx.rhs[mt1] ?? 0) - ieq;
    if (mt2 >= 0)
        ctx.rhs[mt2] = (ctx.rhs[mt2] ?? 0) + ieq;
}
/** Pin the state row (internal node) to a specific value. */
function pinStateRow(ctx, stateRow, value) {
    ctx.addY(stateRow, stateRow, 1);
    ctx.rhs[stateRow] = value;
}
function validateParams(component) {
    const gOff = getNumericParam(component, "gOff", G_OFF_DEFAULT);
    const rOn = getNumericParam(component, "rOn", R_ON_DEFAULT);
    const vOn = getNumericParam(component, "vOn", V_ON_DEFAULT);
    const vFLed = getNumericParam(component, "vFLed", V_F_LED_DEFAULT);
    const gLed = getNumericParam(component, "gLed", G_LED_DEFAULT);
    const iFTrig = getNumericParam(component, "iFTrig", I_F_TRIG_DEFAULT);
    const vZc = getNumericParam(component, "vZc", V_ZC_DEFAULT);
    const vHoldProxy = getNumericParam(component, "vHoldProxy", V_HOLD_VOLTAGE_PROXY_DEFAULT);
    const optoCtx = { stampType: "optocoupler-triac", componentId: component.id };
    if (!(gOff > 0) || !Number.isFinite(gOff)) {
        throw new StampParameterError(`optocouplerTriac: component "${component.id}" has non-positive gOff (got ${String(gOff)})`, { meta: { ...optoCtx, parameter: "gOff", received: gOff, expected: "> 0 and finite" } });
    }
    if (!(rOn > 0) || !Number.isFinite(rOn)) {
        throw new StampParameterError(`optocouplerTriac: component "${component.id}" has non-positive rOn (got ${String(rOn)})`, { meta: { ...optoCtx, parameter: "rOn", received: rOn, expected: "> 0 and finite" } });
    }
    if (!(gLed > 0) || !Number.isFinite(gLed)) {
        throw new StampParameterError(`optocouplerTriac: component "${component.id}" has non-positive gLed (got ${String(gLed)})`, { meta: { ...optoCtx, parameter: "gLed", received: gLed, expected: "> 0 and finite" } });
    }
    if (!(iFTrig > 0) || !Number.isFinite(iFTrig)) {
        throw new StampParameterError(`optocouplerTriac: component "${component.id}" has non-positive iFTrig (got ${String(iFTrig)})`, { meta: { ...optoCtx, parameter: "iFTrig", received: iFTrig, expected: "> 0 and finite" } });
    }
    if (!(vZc > 0) || !Number.isFinite(vZc)) {
        throw new StampParameterError(`optocouplerTriac: component "${component.id}" has non-positive vZc (got ${String(vZc)})`, { meta: { ...optoCtx, parameter: "vZc", received: vZc, expected: "> 0 and finite" } });
    }
    return { gOff, rOn, vOn, vFLed, gLed, iFTrig, vZc, vHoldProxy };
}
/**
 * DC stamp — photo-TRIAC starts OFF (state row = 0); LED branch
 * always linear. No latch logic at DC OP.
 */
export const optocouplerTriacDCStamp = (ctx, component) => {
    const p = validateParams(component);
    const anode = ctx.nodeIndex(getTerminalNetId(component, "anode"));
    const cathode = ctx.nodeIndex(getTerminalNetId(component, "cathode"));
    const mt1 = ctx.nodeIndex(getTerminalNetId(component, "mt1"));
    const mt2 = ctx.nodeIndex(getTerminalNetId(component, "mt2"));
    const stateRow = ctx.internalNodeIndex(component.id, 0);
    stampLedBranch(ctx, anode, cathode, p.gLed, p.vFLed);
    stampPhotoOffBranch(ctx, mt1, mt2, p.gOff);
    pinStateRow(ctx, stateRow, 0);
};
/**
 * Transient stamp — state evolves via LED current trigger +
 * zero-crossing gating + holding-current latch (TRIAC pattern reuse).
 */
export const optocouplerTriacTransientStamp = (ctx, component) => {
    const p = validateParams(component);
    const anode = ctx.nodeIndex(getTerminalNetId(component, "anode"));
    const cathode = ctx.nodeIndex(getTerminalNetId(component, "cathode"));
    const mt1 = ctx.nodeIndex(getTerminalNetId(component, "mt1"));
    const mt2 = ctx.nodeIndex(getTerminalNetId(component, "mt2"));
    const stateRow = ctx.internalNodeIndex(component.id, 0);
    let newState = 0;
    let vDiffPrev = 0;
    if (!ctx.firstStep) {
        const prevStateRaw = nodeVoltage(ctx.xStepPrev, stateRow);
        const prevState = prevStateRaw > 0.5 ? 1 : 0;
        const vAnodePrev = nodeVoltage(ctx.xStepPrev, anode);
        const vCathodePrev = nodeVoltage(ctx.xStepPrev, cathode);
        const vLedDrop = vAnodePrev - vCathodePrev;
        // LED current proxy: above-knee linear conduction.
        const iLedProxy = vLedDrop > p.vFLed ? (vLedDrop - p.vFLed) * p.gLed : 0;
        const vMt1Prev = nodeVoltage(ctx.xStepPrev, mt1);
        const vMt2Prev = nodeVoltage(ctx.xStepPrev, mt2);
        vDiffPrev = vMt1Prev - vMt2Prev;
        const inZeroCrossWindow = Math.abs(vDiffPrev) < p.vZc;
        const ledTrigger = iLedProxy > p.iFTrig && inZeroCrossWindow;
        const stillLatched = prevState === 1 && Math.abs(vDiffPrev) > p.vHoldProxy;
        newState = ledTrigger || stillLatched ? 1 : 0;
    }
    stampLedBranch(ctx, anode, cathode, p.gLed, p.vFLed);
    pinStateRow(ctx, stateRow, newState);
    if (newState === 1) {
        stampPhotoOnBranch(ctx, mt1, mt2, p.rOn, p.vOn, vDiffPrev);
    }
    else {
        stampPhotoOffBranch(ctx, mt1, mt2, p.gOff);
    }
};
/**
 * Single envelope — DC + transient applies. Dynamic augment 1
 * internal node (state row).
 */
export const optocouplerTriacDCStampDefinition = defineStamp({
    type: "optocoupler-triac",
    augments: [],
    dynamicAugments: mocDynamicAugments,
    applyDC: optocouplerTriacDCStamp,
    applyTransient: optocouplerTriacTransientStamp,
});
export const optocouplerTriacTransientStampDefinition = optocouplerTriacDCStampDefinition;
//# sourceMappingURL=optocoupler-triac.js.map